import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Dropdown,
  DropdownBody,
  DropdownItem,
  getColor,
  Icons,
  InfiniteScrollBox,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "../index.scss";
import useDocumentStore from "../../../redux/dispatcher/useDocumentStore";
import { DocInfo, Folder, FolderInfoDTO } from "../../../types/files";
import utilsDocs from "../../../shared/utils/docs";
import classNames from "classnames";
import utilsCommon from "../../../shared/utils/common";
import RevisionTag from "../../../components/RevisionTag";
import DateDropdown from "../../../components/DateDropdown";
import dayjs from "dayjs";
import { getUserInfoFromLocalStorage } from "../../../shared/utils/auth";
import { LoginDto } from "../../../types/auth";
import EmptyInitFilterPage from "../../../components/EmptyPage/EmptyInitFilterPage";
import {
  doUnauthDocDownload,
  handleDocDownload,
  handleDocPreview,
} from "../../../shared/utils/api";
import useStartDownload from "../../../shared/hooks/useStartDownload";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
const sort_by = [
  { resource: t("common.sortDateNewFirst"), key: "datedesc" },
  { resource: t("common.sortDateNewLast"), key: "date" },
  { resource: t("common.sortDateNameDesc"), key: "filedesc" },
  { resource: t("common.sortDateNameAsc"), key: "file" },
];
const FILE_READ_REQUEST_LIST_CLIENT = [
  { key: "REQ", resource: t("file.filter.onlyRequestClient"), value: true },
  { key: "ALL", resource: t("file.filter.all"), value: false },
];
const FILE_MENU_LIST_CLIENT = [
  { key: "ALL", resource: t("common.period.all") },
  { key: "FAV", resource: t("bookmark.title") },
  { key: "MINE", resource: t("file.filter.masterClient") },
  { key: "UNREAD", resource: t("file.filter.unread") },
];
const ClientFilePage = ({
  workgroupId,
  refreshCnt,
}: {
  workgroupId: string;
  refreshCnt: number;
}) => {
  const {
    documentState,
    getContentList,
    getContentFavorite,
    getReadRequestContentList,
  } = useDocumentStore();
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";
  const INIT_FILTER = {
    page: 0,
    size: 50,
    order: "datedesc",
    search: "",
    readType: "",
    masterUserId: "",
    date: {
      start: 0,
      end: 0,
    },
    tagCodes: [workgroupId, "ROOT"],
    folderCode: workgroupId,
  };
  const { t } = useTranslation();

  const startDownload = useStartDownload();
  const inputRef = useRef<HTMLTableRowElement>(null);
  const documentListNewCard = useRef([] as any[]);
  const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'
  const { getContentDownload, getDocDecryptDownload } = useDocumentStore();
  const toasts = useToast();
  const [filter, changeFilter] = useState(INIT_FILTER);
  const [buttonClick, setButtonClick] = useState(0);
  const [docList, changeDocList] = useState<DocInfo[]>([]);
  const [key, setKey] = useState(0);
  const [fileTypeFilter, changeFileFilter] = useState("ALL");
  const [dateFilter, changeDateFilter] = useState<string>("all");
  const [isReadRequest, toggleIsReadRequest] = useState(false);
  const [currFolderPath, changeCurrFolderPath] = useState([
    { folderId: workgroupId, folderName: "HOME" },
  ]);
  const [settingButtonInfo, setDocSettingInfo] = useState<{
    x: number;
    y: number;
    syncId: string;
    docInfo?: DocInfo;
  }>({
    x: 0,
    y: 0,
    syncId: "",
    docInfo: undefined,
  });
  useEffect(() => {
    getContentList({
      ...filter,
    });
  }, [filter, refreshCnt]);
  useEffect(() => {
    changeFilter({
      ...INIT_FILTER,
      tagCodes: [workgroupId, "ROOT"],
      folderCode: workgroupId,
      page: 0,
    });
    document.getElementById("docTable")?.scrollTo(0, 0);
    changeCurrFolderPath([{ folderId: workgroupId, folderName: "HOME" }]);
    changeFileFilter("ALL");
    changeDateFilter("all");
    toggleIsReadRequest(false);
  }, [workgroupId]);
  useEffect(() => {
    let newDataCardContent = [] as any[];
    documentState.docListInfo?.folder?.currntFolderList?.forEach(
      (item: FolderInfoDTO, index: number) =>
        newDataCardContent.push(getTableFolderData(item))
    );
    documentState.docListInfo?.docs?.doc.forEach(
      (item: DocInfo, index: number) =>
        newDataCardContent.push(getTableCardData(item, index))
    );
    documentListNewCard.current = newDataCardContent;
    setKey(key + 1);
    changeDocList(documentState.docListInfo?.docs?.doc ?? []);
  }, [documentState.docListInfo]);
  const handleClickOutside = useCallback((e?: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e?.target)) {
      handleTurnOffSettng();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    document.addEventListener("scroll", handleTurnOffSettng, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("scroll", handleTurnOffSettng, true);
    };
  });
  const handleMoveToTargetFolder = (folderId: string) => {
    const index = currFolderPath.findIndex(
      (folder) => folder.folderId === folderId
    );
    changeCurrFolderPath(currFolderPath.filter((item, idx) => idx <= index));
    changeFilter({
      ...filter,
      tagCodes: [workgroupId, folderId === workgroupId ? "ROOT" : folderId],
      folderCode: folderId,
      page: 0,
    });
    document.getElementById("docTable")?.scrollTo(0, 0);
  };
  const handleTurnOffSettng = (): void => {
    setDocSettingInfo({
      x: 0,
      y: 0,
      syncId: "",
      docInfo: undefined,
    });
  };
  const changeFilterByKey = (key: string, value: any) => {
    setButtonClick(buttonClick + 1);
    if (key === "date") {
      if (typeof value !== "object") {
        changeDateFilter(value);
        changeFilter({
          ...filter,
          date: {
            start: utilsCommon.getStartPeriod(value),
            end: dayjs().endOf("day").valueOf(),
          },
          page: 0,
        });
      } else {
        changeDateFilter("custom");
        changeFilter({
          ...filter,
          date: {
            start: dayjs(value.start).valueOf(),
            end: dayjs(value.end).valueOf(),
          },
          page: 0,
        });
      }
      if (isReadRequest) {
        changeFileFilter("ALL");
      }
      toggleIsReadRequest(false);
    } else if (key === "fileType") {
      changeFileFilter(value);
      changeFilter({
        ...filter,
        masterUserId: value === "MINE" ? myId : "",
        readType:
          value === "FAV" ? "favorit" : value === "UNREAD" ? "unread" : "",
        page: 0,
      });
      if (isReadRequest) {
        changeDateFilter("all");
      }
      toggleIsReadRequest(false);
    } else if (key === "readRequest") {
      toggleIsReadRequest(value);
      if (value) {
        changeDateFilter("all");
        changeFileFilter("ALL");
        getReadRequestContentList({
          pos: 0,
          posCount: 50,
          workgroupId: workgroupId,
        });
      } else {
        changeFilter({
          ...filter,
          masterUserId: "",
          readType: "",
          page: 0,
        });
      }
    }
    document.getElementById("docTable")?.scrollTo(0, 0);
  };
  /** 문서 상세정보 모달 열기 기능 */
  const handleOpenClientDocInfo = (syncId: string) => {
    if (utilsCommon.isClientLogin("MAC")) {
      (window as any)?.webkit?.messageHandlers.DocDetail.postMessage(syncId);
    } else if (utilsCommon.isClientLogin("WINDOWS")) {
      window?.chrome?.webview?.hostObjects.client.DocDetail(syncId);
    }
    handleTurnOffSettng();
  };
  /** 문서 다운로드 기능 */
  const handleDocClientDownload = async (syncId: string) => {
    handleDocDownload(
      syncId,
      getContentDownload,
      toasts,
      t,
      "0",
      docList?.filter((item) => item.syncId === syncId)[0]?.contentName ?? ""
    );
    handleTurnOffSettng();
  };
  const getTableFolderData = (row?: FolderInfoDTO) => {
    return {
      id: row?.folderId,
      data: row ? (
        <Box
          classes="clientFile-item cursor pointer"
          onClick={() => {
            changeCurrFolderPath([
              ...currFolderPath,
              { folderId: row?.folderId, folderName: row?.folderName },
            ]);
            changeFilter({
              ...filter,
              tagCodes: [
                workgroupId,
                row?.folderId === workgroupId ? "ROOT" : row?.folderId,
              ],
              folderCode: row?.folderId,
              page: 0,
            });
          }}
        >
          {<Icons variant="document" label={"folder"} size={32} />}
          <Box direction="column" width={"calc(100% - 65px)"} classes="mg ml-8">
            <Box alignItems="center">
              <Tooltip message={row?.folderName} maxWidth={"calc(100% - 25px)"}>
                <Typography type="body2_rg" ellipsis>
                  {row?.folderName}
                </Typography>
              </Tooltip>
            </Box>
            <Box classes="mg mt-6">
              <Typography
                type="caption1_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {utilsCommon.renderDateFormat(row?.updateTime)}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          classes="clientFile-item cursor pointer"
          onClick={() =>
            handleMoveToTargetFolder(
              currFolderPath[currFolderPath?.length - 2]?.folderId
            )
          }
        >
          <Icons variant="line" label="foldermove" size={20} />
          <Box
            width={"calc(100% - 65px)"}
            classes="mg ml-8"
            alignItems="center"
          >
            <Typography type="body2_rg" ellipsis>
              {t("file.moveToUpperFolder")}
            </Typography>
          </Box>
        </Box>
      ),
    };
  };
  const getTableCardData = (row: DocInfo, index: any) => {
    return {
      id: row?.syncId,
      data: (
        <Box classes="clientFile-item">
          {utilsDocs.renderDocIcon(row?.contentName, 32)}
          <Box direction="column" width={"calc(100% - 65px)"} classes="mg ml-8">
            <Box alignItems="center">
              <Tooltip
                message={row?.contentName}
                maxWidth={"calc(100% - 25px)"}
              >
                <Typography
                  type="body2_rg"
                  ellipsis
                  onClick={() =>
                    utilsCommon.checkMenuAvailble("preview", row) &&
                    handleDocPreview({
                      permission: "view",
                      workgroupId: row.workgroup.workgroupId,
                      syncId: row.syncId,
                      toast: toasts,
                      t,
                      revisionNo: 0,
                    })
                  }
                  classes={classNames({
                    "hover-text-underline": utilsCommon.checkMenuAvailble(
                      "preview",
                      row
                    ),
                  })}
                >
                  {row?.contentName}
                </Typography>
              </Tooltip>
              {row?.revisionNo > 1 && (
                <RevisionTag
                  classes={"mg ml-4"}
                  size={"sm"}
                  revisionNo={row?.revisionNo}
                />
              )}
            </Box>
            <Box classes="mg mt-6">
              <Tooltip
                message={row?.masterUser?.userName}
                maxWidth={"calc(100% - 125px)"}
              >
                <Typography
                  type="caption1_rg"
                  ellipsis
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {row?.masterUser?.userName}
                </Typography>
              </Tooltip>
              <Typography
                type="caption1_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                classes="mg ml-4"
              >
                {` · ${utilsCommon.renderDateFormat(row?.revisionDate)}`}
              </Typography>
            </Box>
          </Box>
          {row?.authType !== "N" &&
            !!row?.favoritDate &&
            row?.favoritDate > 0 && (
              <Box classes="cursor pointer mg ml-auto">
                <Icons variant="fill" label="star" isColored size={20} />
              </Box>
            )}
          {row?.authType !== "N" && (
            <Box classes="hover-actions">
              <Tooltip
                message={
                  row?.favoritDate > 0
                    ? t("file.action.removeFav")
                    : t("file.action.addFav")
                }
              >
                <Button
                  btnStyle={"table-icon"}
                  onClick={() =>
                    getContentFavorite({
                      syncIds: [row?.syncId],
                      flag: row?.favoritDate <= 0,
                    })
                  }
                  leadingIcon={
                    <Icons
                      variant="fill"
                      label="star"
                      isColored={!!row?.favoritDate && row?.favoritDate > 0}
                      size={20}
                    />
                  }
                />
              </Tooltip>
              <Button
                btnStyle={"table-icon"}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setDocSettingInfo({
                    x: direction ? e.pageX : e.pageX - 120,
                    y:
                      350 > e.pageY
                        ? e.pageY
                        : e.pageY -
                          (utilsCommon.checkMenuAvailble(
                            "unAuthDownload",
                            settingButtonInfo.docInfo,
                            "client"
                          )
                            ? 52
                            : 98),
                    syncId: row?.syncId,
                    docInfo: row,
                  });
                }}
                leadingIcon={<Icons variant="line" label="moreVertical" />}
              />
            </Box>
          )}
        </Box>
      ),
    };
  };
  const renderFileFolderPath = () => {
    return (
      <Box classes="mg ml-20 mb-10" alignItems="center">
        {currFolderPath.map((item, index) =>
          [0, currFolderPath.length - 2, currFolderPath.length - 1].includes(
            index
          ) ? (
            <>
              {index !== 0 && (
                <Typography
                  type="body4_rg"
                  classes="mg ml-4 mr-4"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  /
                </Typography>
              )}
              <Box
                classes="cursor pointer"
                onClick={() => handleMoveToTargetFolder(item.folderId)}
              >
                <Tooltip message={item.folderName}>
                  <Typography
                    maxWidth={68}
                    ellipsis
                    type="body4_rg"
                    exactColor={getColor(
                      index === currFolderPath.length - 1
                        ? "COLOR_TEXT_PRIMARY"
                        : "COLOR_TEXT_SECONDARY"
                    )}
                  >
                    {item.folderName}
                  </Typography>
                </Tooltip>
              </Box>
            </>
          ) : (
            index === currFolderPath.length - 3 && (
              <>
                <Typography
                  type="body4_rg"
                  classes="mg ml-4 mr-4"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  /
                </Typography>
                <Typography
                  type="body4_rg"
                  classes="mg ml-4 mr-4"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  ...
                </Typography>
              </>
            )
          )
        )}
      </Box>
    );
  };
  useEffect(() => {
    const container = document.getElementById("filter");
    if (!container) return;
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    container.addEventListener("mousedown", (e) => {
      isDown = true;
      container.classList.add("active");
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
    });

    container.addEventListener("mouseleave", () => {
      isDown = false;
      container.classList.remove("active");
    });

    container.addEventListener("mouseup", () => {
      isDown = false;
      container.classList.remove("active");
    });

    container.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1; // 드래그 속도 조절 (1 = 기본)
      container.scrollLeft = scrollLeft - walk;
    });
  }, []);
  const renderFileFilter = () => {
    return (
      <Box classes="clientFile-filter" id="filter">
        <Dropdown
          btnStyle="round"
          classes={"mg ml-12"}
          placeholder={t("file.filter.fileType")}
          btnSize="rd"
          buttonWidth={240}
          positionStrategy="fixed"
          value={
            fileTypeFilter === "ALL"
              ? ""
              : FILE_MENU_LIST_CLIENT.filter(
                  (item) => item.key === fileTypeFilter
                )[0].resource
          }
          buttonClick={buttonClick}
        >
          {FILE_MENU_LIST_CLIENT.map((item) => (
            <DropdownItem
              label={item.resource}
              type="check"
              checked={item.key === fileTypeFilter}
              onClick={() => changeFilterByKey("fileType", item.key)}
              checkColor={item.key === fileTypeFilter}
              state="selected"
            />
          ))}
        </Dropdown>
        <DateDropdown
          filter={filter}
          buttonClick={buttonClick}
          showValue={false}
          positionStrategy="fixed"
          setButtonClick={setButtonClick}
          dateFilter={dateFilter}
          changeFilterByKey={changeFilterByKey}
          isClient
        />
        <Dropdown
          btnStyle="round"
          classes={"mg ml-12"}
          positionStrategy="fixed"
          btnSize="rd"
          placeholder={t("header.setAlarm.request")}
          buttonWidth={224}
          align="right"
          // showValue
          value={
            !isReadRequest ? "" : FILE_READ_REQUEST_LIST_CLIENT[0].resource
          }
          buttonClick={buttonClick}
        >
          {FILE_READ_REQUEST_LIST_CLIENT.map((item) => (
            <DropdownItem
              label={item.resource}
              type="check"
              checked={item.value === isReadRequest}
              onClick={() => changeFilterByKey("readRequest", item.value)}
              checkColor={item.value === isReadRequest}
              state="selected"
            />
          ))}
        </Dropdown>
      </Box>
    );
  };
  return (
    <Box classes="clientFile clientWorkgroupBody">
      <Box classes="clientWorkgroupBody-header">
        <Dropdown
          btnStyle="link"
          placeholder="asdf"
          buttonClick={buttonClick}
          value={
            sort_by.filter((item) => item.key === filter.order)[0].resource
          }
        >
          {sort_by.map((item) => (
            <DropdownItem
              label={item.resource}
              type="check"
              checked={item.key === filter.order}
              checkColor={item.key === filter.order}
              onClick={() => {
                document.getElementById("docTable")?.scrollTo(0, 0);
                changeFilter({ ...filter, order: item.key, page: 0 });
                setButtonClick(buttonClick + 1);
              }}
            />
          ))}
        </Dropdown>
      </Box>
      {renderFileFilter()}
      {currFolderPath.length > 1 && !isReadRequest && renderFileFolderPath()}
      {currFolderPath.length > 1 && !isReadRequest && (
        <Box
          classes="clientFile-item cursor pointer mg ml-20"
          onClick={() =>
            handleMoveToTargetFolder(
              currFolderPath[currFolderPath?.length - 2]?.folderId
            )
          }
        >
          <Icons variant="line" label="foldermove" size={20} />
          <Box
            width={"calc(100% - 65px)"}
            classes="mg ml-8"
            alignItems="center"
          >
            <Typography type="body2_rg" ellipsis>
              {t("file.moveToUpperFolder")}
            </Typography>
          </Box>
        </Box>
      )}
      {documentState.docListInfo?.docs?.doc?.length === 0 &&
      documentState.docListInfo?.folder?.currntFolderList?.length === 0 ? (
        <EmptyInitFilterPage
          pageType={isReadRequest ? "clientReadRequest" : "client"}
        />
      ) : (
        <InfiniteScrollBox
          data={documentListNewCard.current}
          height={
            currFolderPath.length > 1 && !isReadRequest ? "312px" : "390px"
          }
          id="docTable"
          width="100%"
          endReached={() =>
            docList.length < documentState.docListInfo.total &&
            changeFilter({ ...filter, page: filter.page + 1 })
          }
        />
      )}
      {settingButtonInfo.syncId && (
        <DropdownBody
          align="right"
          minWidth={"120px"}
          dropdownRef={inputRef}
          location={{ x: settingButtonInfo.x, y: settingButtonInfo.y }}
        >
          {utilsCommon.checkMenuAvailble(
            "unAuthDownload",
            settingButtonInfo.docInfo,
            "client"
          ) ? (
            <DropdownItem
              label={t("file.action.download")}
              frontIcon={<Icons variant="line" label="download" />}
              onClick={() => {
                settingButtonInfo.docInfo &&
                  doUnauthDocDownload(
                    settingButtonInfo.docInfo,
                    toasts,
                    getDocDecryptDownload,
                    startDownload
                  );
                handleTurnOffSettng();
              }}
            />
          ) : (
            <>
              <DropdownItem
                label={t("file.action.download")}
                frontIcon={<Icons variant="line" label="download" />}
                onClick={() =>
                  handleDocClientDownload(settingButtonInfo.syncId)
                }
              />
              <DropdownItem
                label={t("file.detail")}
                frontIcon={<Icons variant="line" label="info" />}
                onClick={() =>
                  handleOpenClientDocInfo(settingButtonInfo.syncId)
                }
              />
            </>
          )}
        </DropdownBody>
      )}
    </Box>
  );
};

export default ClientFilePage;
